import { FC, useMemo } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { ComboboxMulti } from "@/components/elements/Combobox";
import { text } from "@/theme/colors";
import { useGetNationalityList } from "@/api/services/global/master";
import { useEnum } from "@/api/services/global/enum";
import { useGetProvinceAndMunicipalityList } from "@/hooks/useGetProvinceAndMunicipalityList";
import { TOption } from "@/types/common";
import { TLearnerReportFilterForm } from "@/features/learning-progress/utils/learner-report-filter-shared";

const COLUMN_SIMPLE = 4;
const COLUMN_RANGE = 8;

export const LearnerReportFilterFormFields: FC = () => {
  const { nationalityOptions } = useGetNationalityList();
  const { provinceOptions } = useGetProvinceAndMunicipalityList();
  const { data: genderEnumOptions } = useEnum("EGender");
  const { control } = useFormContext<TLearnerReportFilterForm>();

  const nationalityStringOptions = useMemo<TOption[]>(
    () => nationalityOptions.map((opt) => ({ ...opt, value: String(opt.value) })),
    [nationalityOptions],
  );
  const genderStringOptions = useMemo<TOption[]>(
    () => genderEnumOptions.map((opt) => ({ value: String(opt.value), label: opt.label })),
    [genderEnumOptions],
  );

  return (
    <Grid container rowSpacing={2} columnSpacing={3}>
      <Grid item xs={COLUMN_SIMPLE}>
        <Stack spacing={0.5}>
          <Typography variant="cap12">氏名</Typography>
          <FormField control={control} name="fullName">
            <Input placeholder="入力してください" clearable={false} />
          </FormField>
        </Stack>
      </Grid>
      <Grid item xs={COLUMN_SIMPLE}>
        <Stack spacing={0.5}>
          <Typography variant="cap12">国籍</Typography>
          <Controller
            control={control}
            name="nationalityIds"
            render={({ field }) => (
              <ComboboxMulti
                placeholder="選択してください"
                value={(field.value as string[]) ?? []}
                onChange={field.onChange}
                options={nationalityStringOptions}
              />
            )}
          />
        </Stack>
      </Grid>
      <Grid item xs={COLUMN_SIMPLE}>
        <Stack spacing={0.5}>
          <Typography variant="cap12">性別</Typography>
          <Controller
            control={control}
            name="genders"
            render={({ field }) => (
              <ComboboxMulti
                placeholder="選択してください"
                value={(field.value as string[]) ?? []}
                onChange={field.onChange}
                options={genderStringOptions}
              />
            )}
          />
        </Stack>
      </Grid>

      <Grid item xs={COLUMN_SIMPLE}>
        <Stack spacing={0.5}>
          <Typography variant="cap12">居住地</Typography>
          <Controller
            control={control}
            name="prefectures"
            render={({ field }) => (
              <ComboboxMulti
                placeholder="選択してください"
                value={(field.value as string[]) ?? []}
                onChange={field.onChange}
                options={provinceOptions}
              />
            )}
          />
        </Stack>
      </Grid>
      <Grid item xs={COLUMN_RANGE}>
        <Stack spacing={0.5}>
          <Typography variant="cap12">1号残期間</Typography>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Box flex={1}>
              <FormField control={control} name="residencePeriodMonthsFrom">
                <Input placeholder="入力してください" transformToHalfWidth clearable={false} />
              </FormField>
            </Box>
            <Typography variant="cap12" color={text.secondary} sx={{ pt: "10px" }}>
              〜
            </Typography>
            <Box flex={1}>
              <FormField control={control} name="residencePeriodMonthsTo">
                <Input placeholder="入力してください" transformToHalfWidth clearable={false} />
              </FormField>
            </Box>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
};
